import React, {useEffect, useState} from 'react';
import {ArrowLeft, ArrowRight, Minus, Plus, X, ZoomIn} from 'lucide-react';
import PhotographicLayers from './configurator/PhotographicLayers';
import {Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle} from './ui/dialog';
import {catalogueImageProps, catalogueScene, productMedia} from '../utils/productMedia';
import {useCopy, useLocale} from '../i18n/LocaleContext';
import './ProductMedia.css';

const COPY = {
  fr: {
    loadError:'Le visuel ne peut pas être chargé.', retry:'Réessayer', unavailable:'Visuel indisponible.',
    images:name => `Images de ${name}`, enlarge:'Agrandir', enlargeLabel:(name, view) => `Agrandir ${name}, ${view}`,
    chooseView:'Choisir une vue', viewOf:(view, name) => `${view} de ${name}`, closeZoom:'Fermer l’agrandissement',
    zoomHelp:'Examinez le produit entier ou agrandissez les détails. Échap ferme cette fenêtre.',
    previous:'Vue précédente', next:'Vue suivante', zoomOut:'Réduire le zoom', zoomIn:'Augmenter le zoom', percent:n => `${n} %`,
  },
  en: {
    loadError:'This image couldn’t be loaded.', retry:'Try again', unavailable:'Image unavailable.',
    images:name => `Images of ${name}`, enlarge:'Enlarge', enlargeLabel:(name, view) => `Enlarge ${name}, ${view}`,
    chooseView:'Choose a view', viewOf:(view, name) => `${view} of ${name}`, closeZoom:'Close the enlarged view',
    zoomHelp:'Look at the whole piece or zoom in on the details. Escape closes this window.',
    previous:'Previous view', next:'Next view', zoomOut:'Zoom out', zoomIn:'Zoom in', percent:n => `${n}%`,
  },
};

const API = process.env.REACT_APP_BACKEND_URL || '';

export function ProductPhoto({src, alt, thumbnail = false, original = false, priority = false, interactive = true}) {
  const [status,setStatus] = useState('loading');
  const [attempt,setAttempt] = useState(0);
  const c = useCopy(COPY);
  useEffect(() => {setStatus('loading');setAttempt(0);}, [src]);
  const image = catalogueImageProps(src,
    thumbnail ? '(min-width: 640px) 260px, 46vw' : '(min-width: 1024px) 560px, 92vw', original);
  if (image.src?.startsWith('/api/')) image.src = `${API}${image.src}`;
  return <div className="catalogue-photo" aria-busy={status === 'loading'}>
    {status === 'loading' && <span className="catalogue-loading" aria-hidden="true" />}
    {status !== 'error' && image.src && <img key={`${src}:${attempt}`} {...image}
      alt={alt} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'}
      decoding="async" onLoad={() => setStatus('ready')} onError={() => setStatus('error')}
      className={status === 'ready' ? 'is-loaded' : ''} draggable="false" />}
    {(status === 'error' || !image.src) && <div className="catalogue-image-error" role="status">
      <p>{c.loadError}</p>
      {interactive && <button type="button" onClick={event => {event.stopPropagation();setStatus('loading');setAttempt(n=>n+1);}}>{c.retry}</button>}
    </div>}
  </div>;
}

export function ProductVisual({media, thumbnail = false, original = false, priority = false, interactive = true}) {
  const [attempt,setAttempt] = useState(0);
  const c = useCopy(COPY);
  if (!media) return <div className="catalogue-image-error" role="status">{c.unavailable}</div>;
  if (media.placeholder) return <div className="catalogue-visual catalogue-placeholder" data-media-id={media.id}
    role="img" aria-label={media.alt} style={{'--placeholder-tint': media.placeholder}}><span aria-hidden="true" /></div>;
  return <div className="catalogue-visual" data-media-id={media.id}>
    {media.scene ? <div className="catalogue-layer-stage">
      <PhotographicLayers key={`${media.id}:${attempt}`} scene={catalogueScene(media.scene, original ? 'original' : thumbnail ? 320 : 640)}
        label={media.alt} />
      {interactive && <button className="catalogue-layer-retry" type="button" onClick={event=>{event.stopPropagation();setAttempt(n=>n+1);}}>{c.retry}</button>}
    </div> : <ProductPhoto src={media.src} alt={media.alt} thumbnail={thumbnail} original={original} priority={priority} interactive={interactive} />}
  </div>;
}

export default function ProductGallery({product, transparent = false}) {
  const {locale} = useLocale();
  const c = useCopy(COPY);
  const media = productMedia(product, {transparent, locale});
  const [active,setActive] = useState(0);
  const [open,setOpen] = useState(false);
  const [zoom,setZoom] = useState(1);
  useEffect(()=>{setActive(0);setZoom(1);setOpen(false);},[product.slug]);
  const current = media[active] || media[0];
  const select = index => {setActive(index);setZoom(1);};
  const previous = () => select((active - 1 + media.length) % media.length);
  const next = () => select((active + 1) % media.length);
  return <section className="catalogue-gallery" aria-label={c.images(product.name)}>
    <div className="catalogue-main-frame">
      <ProductVisual media={current} priority />
      {current && <button type="button" className="catalogue-enlarge" onClick={()=>{setZoom(1);setOpen(true);}}
        aria-label={c.enlargeLabel(product.name, current.label.toLowerCase())}><ZoomIn size={18} /> {c.enlarge}</button>}
    </div>
    {media.length > 1 && <div className="catalogue-thumbnails" aria-label={c.chooseView}>
      {media.map((item,index)=><button key={item.id} type="button" aria-pressed={active===index}
        aria-label={c.viewOf(item.label, product.name)} onClick={()=>select(index)}>
        <span className="catalogue-thumb-frame"><ProductVisual media={item} thumbnail interactive={false} /></span>
        <span>{item.label}</span>
      </button>)}
    </div>}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="catalogue-zoom-dialog" style={{width:'min(96vw, 1100px)',maxWidth:'none',height:'min(94dvh, 1000px)'}}>
        <div className="catalogue-zoom-header">
          <DialogTitle>{product.name} — {current?.label}</DialogTitle>
          <DialogClose className="catalogue-control" aria-label={c.closeZoom}><X size={20} /></DialogClose>
        </div>
        <DialogDescription className="sr-only">{c.zoomHelp}</DialogDescription>
        <div className="catalogue-zoom-viewport" data-lenis-prevent>
          <div className="catalogue-zoom-canvas" style={{width:`${zoom*100}%`}}>
            <ProductVisual media={current} original priority />
          </div>
        </div>
        <div className="catalogue-zoom-controls">
          {media.length>1 && <button type="button" className="catalogue-control" aria-label={c.previous} onClick={previous}><ArrowLeft size={19}/></button>}
          <div className="catalogue-zoom-level">
            <button type="button" className="catalogue-control" aria-label={c.zoomOut} disabled={zoom<=1} onClick={()=>setZoom(n=>Math.max(1,n-0.5))}><Minus size={18}/></button>
            <output aria-live="polite">{c.percent(Math.round(zoom*100))}</output>
            <button type="button" className="catalogue-control" aria-label={c.zoomIn} disabled={zoom>=2.5} onClick={()=>setZoom(n=>Math.min(2.5,n+0.5))}><Plus size={18}/></button>
          </div>
          {media.length>1 && <button type="button" className="catalogue-control" aria-label={c.next} onClick={next}><ArrowRight size={19}/></button>}
        </div>
      </DialogContent>
    </Dialog>
  </section>;
}
